/**
 * Deterministic object-shelf placement (compositing spec §5.5).
 *
 * Pure function: given a room's custom shelves and the objects to place (with
 * pre-measured render widths), it returns each object's frame-pixel position.
 * The two default shelves are synthesised here. Sprite *measurement* (content
 * bounds, Tier-1 seating) happens in the renderer, which feeds widths in — so
 * this stays free of Phaser and trivially testable.
 *
 * Determinism (plan §5): placement is a pure function of (shelves, sorted object
 * ids, widths). A dropped object re-derives the identical layout, so no pixel
 * positions are ever stored.
 */

const FRAME_W = 1280;
const FRAME_H = 720;

/** Shelf rect in (x%, y%-from-bottom) — matches the compiled wire format. */
export interface Shelf {
  name: string;
  priority: number;
  z: string; // "above" | "below"
  ll: [number, number]; // lower-left (x1, y1)
  ur: [number, number]; // upper-right (x2, y2)
}

export interface PlacementItem {
  id: string;
  width: number;  // rendered content width in px
  height: number; // rendered content height in px (reserved for vertical fit)
}

export interface Placement {
  id: string;
  x: number;     // frame px, sprite centre
  y: number;     // frame px (CSS top-origin), the shelf-line contact point
  layer: string; // "above" | "below"
}

export interface PlacementResult {
  placed: Placement[];
  overflow: string[]; // ids that did not fit any shelf
}

/** The two always-present default shelves — pushed to the bottom 1%–10% band so
 * they clear the (raised) dialogue box, edges preserving the centre third for
 * character sprites (spec §5.3). The shared band with the quick menu is fine: the
 * quick menu stays hidden and object sprites take no direct mouse interaction. */
export const DEFAULT_SHELVES: Shelf[] = [
  { name: "left_shelf", priority: 1, z: "above", ll: [5, 1], ur: [35, 10] },
  { name: "right_shelf", priority: 0, z: "above", ll: [65, 1], ur: [95, 10] },
];

export function placeObjects(customShelves: Shelf[], items: PlacementItem[]): PlacementResult {
  // Custom + default shelves as fill "lanes", sorted by descending priority
  // (higher fills first). Custom shelves carry higher priorities than the
  // defaults (1, 0) by convention, so they fill before the defaults.
  const lanes = [...customShelves, ...DEFAULT_SHELVES]
    .map((s) => ({
      priority: s.priority,
      z: s.z,
      rightPx: (s.ur[0] / 100) * FRAME_W,
      cursor: (s.ll[0] / 100) * FRAME_W,
      contactY: (1 - s.ll[1] / 100) * FRAME_H,
    }))
    .sort((a, b) => b.priority - a.priority);

  // Objects placed in a stable, deterministic order (alphabetical by id).
  const sorted = [...items].sort((a, b) => a.id.localeCompare(b.id));

  const placed: Placement[] = [];
  const overflow: string[] = [];

  for (const item of sorted) {
    const slot = item.width + Math.max(item.width * 0.1, 6); // 10% gap, min 6px
    const lane = lanes.find((l) => l.rightPx - l.cursor >= slot);
    if (!lane) {
      overflow.push(item.id);
      continue;
    }
    placed.push({
      id: item.id,
      x: lane.cursor + item.width / 2, // bottom-centre origin → centre of the slot's object
      y: lane.contactY,
      layer: lane.z,
    });
    lane.cursor += slot;
  }

  return { placed, overflow };
}
